import {
    UIDealDetailsTabs,
    useClientSettingStore,
    useStore,
    useStoreUI
} from '@assets/model/Store';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useState } from 'react';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { Flex } from '../UI/Flex/Flex';
import * as style from './DealViewSidebar.module.less';
import { GetIcon } from '../Icons/Icons';

type MenuEntry = { label: string; tabId: UIDealDetailsTabs };
interface CollapseableMenuProps {
    title: string;
    menuItems: MenuEntry[];
    isExpanded: boolean;
}

const CollapseableMenu = observer((p: CollapseableMenuProps) => {
    const uiStore = useStoreUI();

    const getIconForMenuItem = (tabId: UIDealDetailsTabs): string => {
        switch (tabId) {
            case UIDealDetailsTabs.DASHBOARD:
                return GetIcon('menu_dashboard');
            case UIDealDetailsTabs.CHAT:
                return GetIcon('menu_aichat');
            case UIDealDetailsTabs.WORKSTREAMS:
                return GetIcon('menu_workstreams');
            case UIDealDetailsTabs.OUTPUTS:
                return GetIcon('menu_outputs');
            case UIDealDetailsTabs.TASK_VIEW_DEBUG:
                return GetIcon('menu_flowise_debug');
            case UIDealDetailsTabs.DETAILS:
                return GetIcon('menu_deal_details');
            case UIDealDetailsTabs.SCORE_BREAKDOWN:
                return GetIcon('menu_score_details');
            case UIDealDetailsTabs.COMPARE:
                return GetIcon('menu_benchmark');
            default:
                return GetIcon('menu_flowise_debug');
        }
    };

    const makeMenuItem = (tab: UIDealDetailsTabs, label: string) => {
        const isSelected = uiStore.dealViewTab === tab;
        const icon = getIconForMenuItem(tab);
        return (
            <div key={tab}>
                <Flex
                    direction="row"
                    alignItems="center"
                    justify={p.isExpanded ? 'flex-start' : 'center'}
                    style={{ width: '100%' }}
                    className={classNames(style.groupLink, {
                        [style.groupLinkSelected]: isSelected
                    })}
                    onClick={() => {
                        uiStore.dealViewTab = tab;
                    }}
                >
                    <img
                        src={icon}
                        style={{
                            width: '18px',
                            flexShrink: 0,
                            marginRight: p.isExpanded ? '8px' : '0px',
                            marginLeft: p.isExpanded ? '4px' : '0px',
                            transition: 'margin 0.2s ease'
                        }}
                    />
                    <span
                        style={{
                            opacity: p.isExpanded ? 1 : 0,
                            maxWidth: p.isExpanded ? '180px' : '0px',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            transition:
                                'opacity 0.15s ease, max-width 0.2s ease'
                        }}
                    >
                        {label}
                    </span>
                </Flex>
                <div className={style.horizLine}></div>
            </div>
        );
    };

    const menuItems = p.menuItems.map(item =>
        makeMenuItem(item.tabId, item.label)
    );

    return (
        <Flex direction="column" style={{ marginBottom: '10px' }}>
            <div
                style={{
                    maxHeight: p.isExpanded ? '30px' : '0px',
                    opacity: p.isExpanded ? 1 : 0,
                    overflow: 'hidden',
                    marginBottom: p.isExpanded ? '4px' : '0px',
                    transition:
                        'max-height 0.2s ease, opacity 0.15s ease, margin-bottom 0.2s ease'
                }}
            >
                <span className={classNames(style.groupTitle)}>{p.title}</span>
            </div>
            <Flex direction="column">
                <div
                    className={style.horizLine}
                    style={{
                        opacity: p.isExpanded ? 1 : 0,
                        transition: 'opacity 0.15s ease'
                    }}
                ></div>
                {menuItems}
            </Flex>
        </Flex>
    );
});

const DealViewSidebar = observer(() => {
    const store = useStore();
    const clientStore = useClientSettingStore();
    const [isExpanded, setIsExpanded] = useState(false);

    const do_we_have_pdf = store.query.report_has_pdf;
    const aiConsultMenu: MenuEntry[] = [];
    aiConsultMenu.push({
        label: 'Workstreams',
        tabId: UIDealDetailsTabs.WORKSTREAMS
    });
    if (clientStore.showOutputsTab) {
        aiConsultMenu.push({
            label: 'Outputs',
            tabId: UIDealDetailsTabs.OUTPUTS
        });
    }

    if (store.isDev) {
        aiConsultMenu.push({
            label: 'Task View Debug',
            tabId: UIDealDetailsTabs.TASK_VIEW_DEBUG
        });
    }

    if (store.isAdmin) {
        aiConsultMenu.push({
            label: 'Flowise Debug',
            tabId: UIDealDetailsTabs.FLOWISE
        });
    }

    const isRagEnabled = clientStore.showRagChat;
    const chatMenuTitle = isRagEnabled ? 'AI Chat' : 'Case Files';
    return (
        <Offcanvas
            show={true}
            enforceFocus={false}
            backdrop={false}
            className={isExpanded ? 'sidebar-expanded' : ''}
            onMouseEnter={() => {
                setIsExpanded(true);
                document.documentElement.style.setProperty(
                    '--current-sidebar-width',
                    '230px'
                );
            }}
            onMouseLeave={() => {
                setIsExpanded(false);
                document.documentElement.style.setProperty(
                    '--current-sidebar-width',
                    '56px'
                );
            }}
        >
            <Offcanvas.Body>
                <Flex
                    direction="column"
                    style={{ paddingLeft: isExpanded ? '5px' : '0px' }}
                >
                    <CollapseableMenu
                        title="Deal Overview"
                        isExpanded={isExpanded}
                        menuItems={[
                            {
                                label: 'Dashboard',
                                tabId: UIDealDetailsTabs.DASHBOARD
                            },
                            {
                                label: chatMenuTitle,
                                tabId: UIDealDetailsTabs.CHAT
                            }
                        ]}
                        key="deal_overview_menu"
                    />
                    {do_we_have_pdf && (
                        <CollapseableMenu
                            title="CIM Analysis"
                            isExpanded={isExpanded}
                            menuItems={[
                                {
                                    label: 'Deal Details',
                                    tabId: UIDealDetailsTabs.DETAILS
                                },
                                {
                                    label: 'Benchmark & Comps',
                                    tabId: UIDealDetailsTabs.COMPARE
                                },
                                {
                                    label: 'Score Details',
                                    tabId: UIDealDetailsTabs.SCORE_BREAKDOWN
                                }
                            ]}
                            key="cim_analysis_menu"
                        />
                    )}
                    <CollapseableMenu
                        title="AI Consulting"
                        isExpanded={isExpanded}
                        menuItems={aiConsultMenu}
                        key="ai_consult_menu"
                    />
                </Flex>
            </Offcanvas.Body>
        </Offcanvas>
    );
});

export default DealViewSidebar;
